import TextField from "@material-ui/core/TextField";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { Row, Col } from "react-grid-system";
import { useDispatch, useSelector } from "react-redux";

import { disputePayPeriod as employerDisputePayPeriod } from "../../../../actions/employers/payPeriodActions";
import { disputePayPeriod as recruiterDisputePayPeriod } from "../../../../actions/recruiters/payPeriodActions";
import { clearModal } from "../../../../actions/shared/uiActions";

import { makeGetCandidate } from "../../../../selectors/candidateSelectors";
import { makeGetFilteredContracts } from "../../../../selectors/contractSelector";
import { getCurrentIndividual } from "../../../../selectors/individualSelectors";
import { makeGetPayPeriodItems } from "../../../../selectors/payPeriodSelectors";
import { disabledProps } from "../../../../util/formHelpers";

import ActionModalContainer from "../../../General/ActionModal/ActionModalContainer";
import ActionModalContent from "../../../General/ActionModal/ActionModalContent";
import ActionModalFooter from "../../../General/ActionModal/ActionModalFooter";
import ActionModalHeader from "../../../General/ActionModal/ActionModalHeader";

import useSnackbar from "../../../General/useSnackbar";

function formatDate(date) {
  return moment(date).format("ddd. MMM D");
}

export default function DisputePayPeriodModal({
  payPeriodId,
  editedAt,
  setError: setButtonError,
}) {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [explanationReason, setExplanationReason] = useState("");
  const [explanationReasonErrors, setExplanationReasonErrors] = useState([]);
  const [submitCount, setSubmitCount] = useState(0);
  const [error, setError] = useState("");

  const currentIndividual = useSelector((state) => getCurrentIndividual(state));
  const isRecruiter = currentIndividual.recruiter;

  const disputePayPeriod = isRecruiter
    ? recruiterDisputePayPeriod
    : employerDisputePayPeriod;

  const getPayPeriods = useMemo(makeGetPayPeriodItems, []);
  const payPeriod = useSelector((state) => getPayPeriods(state)[payPeriodId]);

  const getContracts = useMemo(makeGetFilteredContracts, []);
  const contract = useSelector(
    (state) =>
      getContracts(state, { contractId: payPeriod?.attributes?.contractId })[0]
  );

  const getCandidate = useMemo(makeGetCandidate, []);
  const candidate = useSelector((state) =>
    getCandidate(state, contract?.attributes?.recruiterSubmissionId)
  );

  const disputeText = useMemo(() => {
    const isWFM = contract?.attributes?.ukgPersonNumber;
    if (isWFM) {
      return (
        <div>
          Disputing will put a hold on invoicing for this timesheet and move it
          to the "Disputed" page. From there, you can "Revise" the timesheet
          once it has been updated and approved in WFM.
        </div>
      );
    } else {
      return (
        <div>
          Disputing will put a hold on invoicing for this timesheet and move it
          to the "Disputed" page. You will be notified when the timesheet has
          been resubmitted for you to review.
        </div>
      );
    }
  }, [isRecruiter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function validateExplanationReason() {
    const errors = [];

    if ((explanationReason?.length || 0) === 0) {
      errors.push("Cannot be blank");
    }

    if ((explanationReason?.length || 0) > 1000) {
      errors.push("Max length of 1000");
    }

    setExplanationReasonErrors(errors);
    return errors;
  }

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitCount(submitCount + 1);
    const errors = await validateExplanationReason();

    if (errors.length === 0) {
      return dispatch(
        disputePayPeriod(payPeriodId, explanationReason, editedAt)
      )
        .then(() => {
          dispatch(clearModal());
          snackbar.showMessage(
            "Timesheet has been disputed and moved to the Disputed Page."
          );
        })
        .catch((error) => {
          setSubmitting(false);
          if (error.response?.status === 409) {
            setError("Hours have been edited. Please refresh.");
            setButtonError("Hours have been edited. Please refresh.");
            snackbar.showMessage("Hours were not approved.");
          } else {
            snackbar.showMessage(
              "An error occurred, try again or contact support."
            );
          }
        });
    }
    setSubmitting(false);
    return false;
  };

  const handleExplanationReason = (event) => {
    setExplanationReason(event.target.value);
  };

  return (
    <ActionModalContainer color="red">
      <ActionModalHeader
        title="Dispute Hours Worked"
        subtitle={`${candidate?.attributes?.firstName} ${candidate?.attributes?.lastName}`}
      />
      <ActionModalContent>
        <p style={{ marginTop: "0px", marginBottom: "50px" }}>
          If the reported weekly hours worked are not accurate, you may begin a
          dispute.
          <br />
          <br />
          {disputeText}
        </p>
        <Row>
          <Col lg={4} xs={12}>
            <TextField
              {...disabledProps(true)}
              label={
                <div className="static-input-label">Contract Worker Name</div>
              }
              value={`${candidate.attributes.firstName} ${candidate.attributes.lastName}`}
              inputProps={{ "data-cy": "dispute-contract-worker-name" }}
            />
          </Col>
          <Col lg={4} xs={12}>
            <TextField
              {...disabledProps(true)}
              label={<div className="static-input-label">Work Week</div>}
              value={`${formatDate(
                payPeriod.attributes.startDate
              )} - ${formatDate(payPeriod.attributes.endDate)}`}
              inputProps={{ "data-cy": "dispute-contract-work-week" }}
            />
          </Col>
          <Col lg={4} xs={12}>
            <TextField
              {...disabledProps(true)}
              label={<div className="static-input-label">Reported By</div>}
              value={payPeriod.attributes.submitterName}
              inputProps={{ "data-cy": "dispute-reporter" }}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={4} xs={12}>
            <TextField
              {...disabledProps(true)}
              label={<div className="static-input-label">Standard Hours</div>}
              value={`${payPeriod.attributes.standardHours || 0}`}
              inputProps={{ "data-cy": "dispute-pay-period-standard-hours" }}
            />
          </Col>
          {!payPeriod.attributes.automatedHoursSubmission && (
            <>
              <Col lg={4} xs={12}>
                <TextField
                  {...disabledProps(true)}
                  label={
                    <div className="static-input-label">Overtime Hours</div>
                  }
                  value={`${payPeriod.attributes.nonStandardHours || 0}`}
                  inputProps={{
                    "data-cy": "dispute-pay-period-overtime-hours",
                  }}
                />
              </Col>
              <Col lg={4} xs={12}>
                <TextField
                  {...disabledProps(true)}
                  label={
                    <div className="static-input-label">Double Time Hours</div>
                  }
                  value={`${payPeriod.attributes.doubleTimeHours || 0}`}
                  inputProps={{
                    "data-cy": "dispute-pay-period-double-time-hours",
                  }}
                />
              </Col>
            </>
          )}
        </Row>
        <Row>
          <Col lg={8} xs={12}>
            <TextField
              label={
                <div className="static-input-label">
                  Explanation of the dispute
                </div>
              }
              error={explanationReasonErrors.length > 0}
              helperText={explanationReasonErrors.join(", ")}
              multiline
              value={explanationReason}
              inputProps={{ "data-cy": "dispute-explanation" }}
              onChange={handleExplanationReason}
            />
          </Col>
        </Row>
      </ActionModalContent>
      <ActionModalFooter
        actionText="Submit"
        submissionError={
          submitCount > 0 && (error || explanationReasonErrors.length > 0)
        }
        customErrorMessage={error || explanationReasonErrors.join(", ")}
        handleAction={handleSubmit}
        isSubmitting={submitting}
        disabled={!!error}
      />
    </ActionModalContainer>
  );
}
